import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { EmptyState } from '@astryxdesign/core/EmptyState';
import { Button } from '@astryxdesign/core/Button';
import { useToast } from '@astryxdesign/core/Toast';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { SheetHeader } from '@/components/SheetHeader';
import { BookingSummary } from './BookingSummary';
import { publicApi } from '@/lib/api/public';
import { toApiError } from '@/lib/api/errors';
import type { PublicBooking } from '@/lib/api/schemas';
import { forgetContacts, loadContacts, saveContacts, saveDeviceBooking } from '@/lib/device-bookings';
import { useStudio } from '@/features/studio/studio-data';
import { attemptFor, clearDraft, emptyContacts, loadDraft, saveDraft, type BookingDraft, type Contacts } from './draft';
import { stepIndex, useBookingNav, type BookingStep } from './useBookingNav';
import { ServiceStep } from './steps/ServiceStep';
import { TimeStep } from './steps/TimeStep';
import { ContactsStep } from './steps/ContactsStep';
import { ConfirmStep } from './steps/ConfirmStep';
import { DoneStep } from './steps/DoneStep';

const TITLES: Record<BookingStep, string> = {
  service: 'Выберите услугу',
  time: 'Дата и время',
  contacts: 'Ваш автомобиль',
  confirm: 'Проверьте запись',
  done: 'Вы записаны',
};

function initialDraft(slug: string): BookingDraft {
  const saved = loadDraft(slug);
  if (saved) return saved;
  const remembered = loadContacts();
  return { contacts: remembered ? { ...emptyContacts, ...remembered } : emptyContacts, remember: true };
}

/**
 * Sequential bottom sheet: service → date and time → contacts → confirmation → done.
 * Each step is a URL/history entry (Back works), focus moves to the step title, and
 * a retry of the confirmation reuses the same idempotency key and access token.
 */
export function BookingSheet() {
  const studio = useStudio();
  const nav = useBookingNav();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState<BookingDraft>(() => initialDraft(studio.slug));
  const [result, setResult] = useState<{ booking: PublicBooking; token: string } | null>(null);
  const titleRef = useRef<HTMLDivElement | null>(null);
  const step = nav.step;

  useEffect(() => {
    if (step) titleRef.current?.focus({ preventScroll: true });
  }, [step]);

  const updateDraft = (next: BookingDraft) => {
    setDraft(next);
    saveDraft(studio.slug, next);
  };

  const mutation = useMutation({
    mutationFn: async () => {
      if (!nav.serviceId || !nav.startsAt) throw new Error('Не выбраны услуга или время');
      const attempt = attemptFor(draft, nav.serviceId, nav.startsAt);
      updateDraft({ ...draft, attempt });
      const booking = await publicApi.createBooking({
        slug: studio.slug,
        serviceId: nav.serviceId,
        startsAt: nav.startsAt,
        customer: {
          name: draft.contacts.name,
          phone: draft.contacts.phone,
          car: draft.contacts.car,
          plate: draft.contacts.plate || undefined,
          comment: draft.contacts.comment || undefined,
        },
        idempotencyKey: attempt.idempotencyKey,
        accessToken: attempt.accessToken,
      });
      return { booking, token: attempt.accessToken };
    },
    onSuccess: ({ booking, token }) => {
      saveDeviceBooking(studio.slug, { token, bookingId: booking.id });
      if (draft.remember) {
        saveContacts({ name: draft.contacts.name, phone: draft.contacts.phone, car: draft.contacts.car });
      } else {
        forgetContacts();
      }
      clearDraft(studio.slug);
      setDraft({ contacts: draft.remember ? draft.contacts : emptyContacts, remember: draft.remember });
      setResult({ booking, token });
      void queryClient.invalidateQueries({ queryKey: ['availability', studio.slug] });
      void queryClient.invalidateQueries({ queryKey: ['my-bookings', studio.slug] });
      nav.go({ book: 'done' }, { replace: true });
    },
    onError: (error) => {
      const apiError = toApiError(error);
      if (apiError.code === 'slot_unavailable' || apiError.code === 'service_not_found') {
        void queryClient.invalidateQueries({ queryKey: ['availability', studio.slug] });
        updateDraft({ ...draft, attempt: undefined });
        toast({ body: apiError.userMessage, type: 'error', uniqueID: 'slot-taken' });
        nav.go({ book: 'time', at: null });
      }
    },
  });

  const selectedService = nav.serviceId ? studio.services.find((s) => s.id === nav.serviceId) : undefined;
  const open = step !== null;
  const showBack = step === 'time' || step === 'contacts' || step === 'confirm';
  const progress = step && step !== 'done' ? `Шаг ${stepIndex(step) + 1} из 4` : null;

  let body = null;
  if (step === 'service') {
    body = <ServiceStep selectedId={nav.serviceId} onSelect={(id) => nav.go({ book: 'time', svc: id, day: null, at: null })} />;
  } else if (step === 'time') {
    body = nav.serviceId ? (
      <TimeStep
        serviceId={nav.serviceId}
        day={nav.day}
        startsAt={nav.startsAt}
        onDay={(day) => nav.go({ day, at: null }, { replace: true })}
        onPick={(slot) => nav.go({ day: slot.day, at: slot.startsAt }, { replace: true })}
        onNext={() => nav.go({ book: 'contacts' })}
        onChangeService={() => nav.go({ book: 'service' })}
      />
    ) : (
      <ServiceStep selectedId={null} onSelect={(id) => nav.go({ book: 'time', svc: id }, { replace: true })} />
    );
  } else if (step === 'contacts') {
    body = (
      <ContactsStep
        initial={draft.contacts}
        remember={draft.remember}
        onSubmit={(contacts: Contacts, remember: boolean) => {
          updateDraft({ ...draft, contacts, remember });
          mutation.reset();
          nav.go({ book: 'confirm' });
        }}
        summary={
          selectedService ? (
            <BookingSummary
              service={selectedService}
              currency={studio.currency}
              timezone={studio.timezone}
              startsAt={nav.startsAt}
              onChange={() => nav.go({ book: 'time' })}
            />
          ) : null
        }
      />
    );
  } else if (step === 'confirm') {
    body =
      nav.serviceId && nav.startsAt ? (
        <ConfirmStep
          serviceId={nav.serviceId}
          startsAt={nav.startsAt}
          contacts={draft.contacts}
          isSubmitting={mutation.isPending}
          error={
            mutation.isError && !['slot_unavailable', 'service_not_found'].includes(toApiError(mutation.error).code)
              ? mutation.error
              : null
          }
          onConfirm={() => mutation.mutate()}
          onEdit={(target) => nav.go({ book: target })}
        />
      ) : (
        <EmptyState
          title="Время не выбрано"
          actions={<Button label="Выбрать время" onClick={() => nav.go({ book: 'time' })} />}
          isCompact
        />
      );
  } else if (step === 'done') {
    body = result ? (
      <DoneStep
        booking={result.booking}
        token={result.token}
        onClose={nav.close}
        onOpenMy={() => navigate(`/s/${studio.slug}/my`, { replace: true })}
      />
    ) : (
      <EmptyState
        title="Запись сохранена"
        description="Детали — в разделе «Моя запись»."
        actions={
          <Button label="Моя запись" variant="primary" onClick={() => navigate(`/s/${studio.slug}/my`, { replace: true })} />
        }
        isCompact
      />
    );
  }

  return (
    <Drawer showSwipeHandle open={open} onOpenChange={(next) => !next && !mutation.isPending && nav.close()}>
      {/* Utility (not a component-layer rule) so it overrides the Drawer's inset-x-0; on wide screens the sheet is a centred 640px column. */}
      <DrawerContent data-testid="booking-sheet" className="data-[swipe-axis=y]:inset-x-[max(0px,calc((100vw-640px)/2))]">
        <SheetHeader
          ref={titleRef}
          title={step ? TITLES[step] : 'Запись'}
          subtitle={progress}
          description={`Запись в студию ${studio.name}`}
          progress={step && step !== 'done' ? { step: stepIndex(step) + 1, total: 4 } : null}
          onClose={nav.close}
          closeDisabled={mutation.isPending}
          onBack={showBack ? nav.back : null}
        />
        <div className="sheet-body">{body}</div>
      </DrawerContent>
    </Drawer>
  );
}
